import React from 'react';
import { motion } from 'framer-motion';
import { Server, Shield, Zap, Cloud, Clock, Settings, ArrowRight, CheckCircle2 } from 'lucide-react';
import { Link } from 'react-router-dom';

const features = [
  {
    icon: Server,
    title: 'Áreiðanleg hýsing',
    description: 'Örugg og stöðug hýsing með 99.9% uppitíma.'
  },
  {
    icon: Shield,
    title: 'Öryggisafritun',
    description: 'Dagleg öryggisafritun og vöktun.'
  },
  {
    icon: Zap,
    title: 'Hraði og afköst',
    description: 'Hraðvirk þjónusta með SSD diskum.'
  },
  {
    icon: Cloud,
    title: 'Skýjalausn',
    description: 'Skalanleg skýjalausn sem vex með þér.'
  },
  {
    icon: Clock,
    title: '24/7 vöktun',
    description: 'Stöðug vöktun og þjónusta.'
  },
  {
    icon: Settings,
    title: 'Sérsniðin þjónusta',
    description: 'Sérsniðnar lausnir fyrir þínar þarfir.'
  }
];

const packages = [
  {
    id: 'basic',
    name: 'Basic',
    price: '4.990 kr./mán',
    description: 'Fyrir minni vefsíður',
    features: [
      '10 GB diskpláss',
      '100 GB umferð á mánuði',
      '2 netföng',
      'SSL vottun',
      'Dagleg öryggisafritun',
      'DNS stjórnun',
      'cPanel stjórnborð',
      '24/7 vöktun'
    ]
  },
  {
    id: 'business',
    name: 'Business',
    price: '9.990 kr./mán',
    description: 'Fyrir meðalstór fyrirtæki',
    features: [
      '50 GB diskpláss',
      '500 GB umferð á mánuði',
      '5 netföng',
      'SSL vottun',
      'Dagleg öryggisafritun',
      'DNS stjórnun',
      'cPanel stjórnborð',
      '24/7 vöktun',
      'Sérsniðin þjónusta',
      'Forgangsþjónusta'
    ],
    popular: true
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    price: '24.990 kr./mán',
    description: 'Fyrir stærri fyrirtæki',
    features: [
      'Ótakmarkað diskpláss',
      'Ótakmörkuð umferð',
      '10 netföng',
      'SSL vottun',
      'Dagleg öryggisafritun',
      'DNS stjórnun',
      'cPanel stjórnborð',
      '24/7 vöktun',
      'Sérsniðin þjónusta',
      'Forgangsþjónusta',
      'Sérsniðið öryggi',
      'VIP þjónusta'
    ]
  }
];

const Hosting = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <div className="relative overflow-hidden bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
          <motion.div 
            className="text-center max-w-3xl mx-auto"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="text-4xl font-bold text-gray-900 mb-6">
              Örugg og áreiðanleg vefhýsing
            </h1>
            <p className="text-xl text-gray-600 mb-8">
              Við bjóðum upp á örugga og áreiðanlega vefhýsingu með 99.9% uppitímaábyrgð 
              og framúrskarandi þjónustu.
            </p>
            <motion.div
              className="flex flex-col sm:flex-row gap-4 justify-center"
            >
              <Link to="/lausnir/vefhysing/panta">
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="bg-[#EA295A] text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-[#d01d4c] transition-all shadow-lg shadow-[#EA295A]/20 hover:shadow-xl hover:shadow-[#EA295A]/30 flex items-center"
                >
                  Panta vefhýsingu
                  <ArrowRight className="ml-2" />
                </motion.button>
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </div>

      {/* Features Section */}
      <motion.div 
        className="py-24 bg-white"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            className="text-center max-w-3xl mx-auto mb-16"
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Vefhýsing í sérflokki
            </h2>
            <p className="text-xl text-gray-600">
              Við bjóðum upp á heildstæða vefhýsingarþjónustu
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <motion.div 
                key={index}
                className="bg-gray-50 rounded-xl p-8"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ y: -5 }}
              >
                <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-[#EA295A]/10 mb-6">
                  <feature.icon className="w-6 h-6 text-[#EA295A]" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">{feature.title}</h3>
                <p className="text-gray-600">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.div>

      {/* Packages Section */}
      <motion.div 
        className="py-24 bg-gray-50"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            className="text-center max-w-3xl mx-auto mb-16"
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Hýsingarpakkar
            </h2>
            <p className="text-xl text-gray-600">
              Veldu þann pakka sem hentar þínum þörfum
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {packages.map((pkg, index) => (
              <motion.div 
                key={index}
                className={`bg-white rounded-xl p-8 shadow-sm ${
                  pkg.popular ? 'border-2 border-[#EA295A]' : ''
                }`}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ y: -5 }}
              >
                {pkg.popular && (
                  <div className="bg-[#EA295A]/10 text-[#EA295A] text-sm font-medium px-3 py-1 rounded-full inline-block mb-4">
                    Vinsælast
                  </div>
                )}
                <h3 className="text-2xl font-bold text-gray-900 mb-2">{pkg.name}</h3>
                <div className="text-3xl font-bold text-[#EA295A] mb-1">{pkg.price}</div>
                <p className="text-gray-500 text-sm mb-4">verð án VSK</p>
                <p className="text-gray-600 mb-6">{pkg.description}</p>
                <ul className="space-y-3 mb-8">
                  {pkg.features.map((feature, i) => (
                    <li key={i} className="flex items-center text-gray-600">
                      <CheckCircle2 className="w-5 h-5 text-[#EA295A] mr-2" />
                      {feature}
                    </li>
                  ))}
                </ul>
                <Link to="/lausnir/vefhysing/panta">
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className={`w-full px-6 py-3 rounded-lg font-medium flex items-center justify-center ${
                      pkg.popular
                        ? 'bg-[#EA295A] text-white hover:bg-[#d01d4c]'
                        : 'bg-gray-100 text-gray-900 hover:bg-gray-200'
                    } transition-all`}
                  >
                    Panta núna
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </motion.button>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.div>

      {/* CTA Section */}
      <motion.div 
        className="py-24 bg-white"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gradient-to-br from-[#EA295A] to-[#FF6B6B] rounded-2xl p-12 text-center">
            <motion.h2 
              className="text-3xl font-bold text-white mb-6"
            >
              Tilbúin að hefja samstarf?
            </motion.h2>
            <motion.p 
              className="text-xl text-white/90 mb-8 max-w-2xl mx-auto"
            >
              Láttu okkur hjálpa þér að finna réttu hýsingarlausnina fyrir þitt 
              fyrirtæki
            </motion.p>
            <motion.div>
              <Link to="/lausnir/vefhysing/panta">
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="bg-white text-[#EA295A] px-8 py-4 rounded-lg text-lg font-semibold hover:bg-gray-50 transition-all shadow-lg"
                >
                  Panta vefhýsingu
                </motion.button>
              </Link>
            </motion.div>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default Hosting;